import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  Container,
  InputGroup,
  FormControl,
  Button,
  Row,
  Col,
  Card,
  Navbar,
  Nav,
  Dropdown
} from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { Search } from 'react-bootstrap-icons';
import AlbumDetail from './AlbumDetail';
import SongDetail from './SongDetail';
import ArtistDetail from './ArtistDetail';
import Login from './components/Login';
import Register from './components/Register';
import UserProfile from './components/UserProfile';
import UserProfileView from './components/UserProfileView';
import Settings from './components/Settings';
import HomePage from './components/HomePage';
import SmallSongCard from './components/SmallSongCard';
import SongsListPage from './components/SongsListPage';
import createDataURL from './utils/createDataURL';
  
// import Home from './components/Home'; // Removed as per edit hint

//TODO DELETE BELOW PLEASE!!
//const CLIENT_ID = process.env.REACT_APP_SPOTIFY_CLIENT_ID;
//const CLIENT_SECRET = process.env.REACT_APP_SPOTIFY_CLIENT_SECRET;

function AppContent() {
  const [searchInput, setSearchInput] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [topResult, setTopResult] = useState([]);
  const [artists, setArtists] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Remove showAuth state
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      // Verify token with backend
      fetch('/api/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          // Token is invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          throw new Error('Invalid token');
        }
      })
      .then(user => {
        setUser(user);
        setIsLoading(false);
      })
      .catch(error => {
        console.log('Token verification failed:', error);
        setIsLoading(false);
        // setShowAuth('login'); // This line is removed as per edit hint
      });
    } else {
      setIsLoading(false);
      // setShowAuth('login'); // This line is removed as per edit hint
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    navigate('/');
  };

  const handleRegister = (userData) => {
    setUser(userData);
    navigate('/');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    // setShowAuth('login'); // Remove this line to prevent showing login page after logout
    navigate('/'); // Redirect to home page after logout
  };

  const switchToRegister = () => {
    navigate('/register');
  };

  const switchToLogin = () => {
    navigate('/login');
  };

  const clearSearch = () => {
    // Only clear the results, keep the search input so user can search again
    setTopResult([]);
    setArtists([]);
    setAlbums([]);
    setTracks([]);
  };

  async function search() {
    const trimmedInput = searchInput.trim();
    if (!trimmedInput) {
      console.warn("Search input is empty or just spaces.");
      return;
    }

    const query = searchInput.trim();
    const resp = await fetch(`/api/spotify/search?q=${encodeURIComponent(query)}`);
    if (!resp.ok) { console.error('Search failed'); return; }
    const data = await resp.json();
    const artistRes = { artists: data.artists };
    const albumRes  = { albums: data.albums };
    const trackRes  = { tracks: data.tracks };

    console.log("🎤 Artists:", artistRes);
    console.log("💿 Albums:", albumRes);
    console.log("🎵 Tracks:", trackRes);

    // Test preview availability
    if (trackRes.tracks && trackRes.tracks.items.length > 0) {
      const tracksWithPreviews = trackRes.tracks.items.filter(track => track.preview_url).length;
      console.log(`📊 Preview availability test: ${tracksWithPreviews}/${trackRes.tracks.items.length} tracks have previews (${((tracksWithPreviews / trackRes.tracks.items.length) * 100).toFixed(1)}%)`);
      
      // Log first few tracks with their preview status
      trackRes.tracks.items.slice(0, 5).forEach((track, index) => {
        console.log(`🎵 Track ${index + 1}: "${track.name}" by ${track.artists.map(a => a.name).join(', ')} - Preview: ${track.preview_url ? '✅' : '❌'} (Popularity: ${track.popularity})`);
      });
    }

    // Smart top result selection algorithm
    const candidates = [];
    
    // Add top artist if available
    if (artistRes.artists?.items[0]) {
      candidates.push({
        item: artistRes.artists.items[0],
        type: 'artist',
        popularity: artistRes.artists.items[0].popularity,
        relevance: calculateRelevance(artistRes.artists.items[0].name, searchInput)
      });
    }
    
    // Add top album if available
    if (albumRes.albums?.items[0]) {
      candidates.push({
        item: albumRes.albums.items[0],
        type: 'album',
        popularity: albumRes.albums.items[0].popularity,
        relevance: calculateRelevance(albumRes.albums.items[0].name, searchInput)
      });
    }
    
    // Add top track if available
    if (trackRes.tracks?.items[0]) {
      candidates.push({
        item: trackRes.tracks.items[0],
        type: 'track',
        popularity: trackRes.tracks.items[0].popularity,
        relevance: calculateRelevance(trackRes.tracks.items[0].name, searchInput)
      });
    }
    
    // Select the best candidate based on relevance and popularity
    let topResult = null;
    let topResultType = null;
    if (candidates.length > 0) {
      // Sort by relevance first, then by popularity
      candidates.sort((a, b) => {
        if (a.relevance !== b.relevance) {
          return b.relevance - a.relevance; // Higher relevance first
        }
        return b.popularity - a.popularity; // Higher popularity as tiebreaker
      });
      
      topResult = candidates[0].item;
      topResultType = candidates[0].type;
      console.log(`🏆 Selected top result: ${candidates[0].type} - "${topResult.name}" (Relevance: ${candidates[0].relevance}, Popularity: ${candidates[0].popularity})`);
    }

    setTopResult(topResult ? [{ ...topResult, type: topResultType }] : []);
    setArtists(artistRes.artists.items);
    setAlbums(albumRes.albums.items);
    setTracks(trackRes.tracks.items);
    
    // Navigate to home page if not already there, so search results display properly
    if (window.location.pathname !== '/') {
      navigate('/');
    }
  }

  // Helper function to calculate relevance score
  function calculateRelevance(itemName, searchQuery) {
    const itemLower = itemName.toLowerCase();
    const queryLower = searchQuery.toLowerCase();
    
    // Exact match gets highest score
    if (itemLower === queryLower) return 100;
    
    // Starts with query gets high score
    if (itemLower.startsWith(queryLower)) return 90;
    
    // Contains query gets medium score
    if (itemLower.includes(queryLower)) return 70;
    
    // Word boundary match gets lower score
    const queryWords = queryLower.split(' ').filter(word => word.length > 0);
    const itemWords = itemLower.split(' ').filter(word => word.length > 0);
    
    let wordMatches = 0;
    for (const queryWord of queryWords) {
      if (itemWords.some(itemWord => itemWord.includes(queryWord))) {
        wordMatches++;
      }
    }
    
    if (wordMatches > 0) {
      return 50 + (wordMatches / queryWords.length) * 20;
    }
    
    return 0;
  }

const renderCard = (item, type = 'default', isTopResult = false) => {
    const img = item?.images?.[0]?.url || item?.album?.images?.[0]?.url;
    const title = item.name;
    const subtitle = type === 'track' || type === 'album' ? item.artists.map(a => a.name).join(', ') : '';

    const imageClass = type === 'artist' ? 'artist-image' : undefined;

    const cardContent = (
      <>
        <Card.Img variant="top" src={img} className={imageClass}
        />
        <Card.Body>
          <Card.Title style={{ fontSize: '1rem' }}>{title}</Card.Title>
          {subtitle && <Card.Text style={{ fontSize: '0.85rem', color: '#666' }}>{subtitle}</Card.Text>}
          {isTopResult && (
            <Card.Text style={{ 
              fontSize: '0.75rem', 
              color: '#888', 
              fontWeight: 'bold',
              marginTop: '4px',
            }}>
              {type === 'artist' ? 'Artist' : type === 'album' ? 'Album' : 'Song'}
            </Card.Text>
          )}
        </Card.Body>
      </>
    );

    const to =
      type === 'album' ? `/album/${item.id}` :
      type === 'track' ? `/track/${item.id}` :
      type === 'artist' ? `/artist/${item.id}` :
      null;

    return (
      <Card key={item.id} style={{
        width: '100%',                       // fill the slot from the strip
        height: type === 'artist' ? '260px' : 'auto',
        border: 'none',
      }}>
        {to
          ? <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>{cardContent}</Link>
          : cardContent
        }
      </Card>
    );
  };

const renderTopResultCard = (item, type = 'default') => {
  const img = item?.images?.[0]?.url || item?.album?.images?.[0]?.url;
  const title = item.name;
  const subtitle = type === 'track' || type === 'album'
    ? item.artists.map((a) => a.name).join(', ')
    : '';

  const to =
    type === 'album' ? `/album/${item.id}` :
    type === 'track' ? `/track/${item.id}` :
    type === 'artist' ? `/artist/${item.id}` :
    null;

  const content = (
    <Card
      className="songs-and-top-result-card"
      style={{
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
        backgroundColor: '#eae9e9ff', // 👈 light grey background
        border: 'none',             // 👈 removes the default border
        height: '227px',
      }}
    >
      <Card.Img
        className="card-img-top"
        src={img}
        style={{
          width: '227px',
          height: '100%',
          objectFit: 'cover',
        }}
        onError={(e) => e.target.src = createDataURL('🎵', '#cccccc', '#666666', 227, 227)}
      />
      <Card.Body style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        maringLeft: '20rem',
        textAlign: 'left',
        overflow: 'hidden',
        minWidth: 0,
      }}>
        <Card.Title style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{title}</Card.Title>
        {subtitle && <Card.Text style={{ fontSize: '1rem', color: '#666' }}>{subtitle}</Card.Text>}
        <Card.Text style={{ fontSize: '0.85rem', color: '#999' }}>{type.toUpperCase()}</Card.Text>
      </Card.Body>
    </Card>
  );

  return to
    ? <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>{content}</Link>
    : content;
};


  // No longer show auth forms as overlays; use routes instead

  return (
      <div className="App">
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
          {/* Top Navigation Bar */}
          <div style={{ 
            width: '100%',
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #dee2e6',
            padding: '15px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            position: 'relative',
            zIndex: 10
          }}>
            {/* App Name */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Link 
                to="/" 
                onClick={clearSearch}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <h3 style={{ 
                  margin: 0, 
                  color: '#333',
                  cursor: 'pointer',
                  transition: 'color 0.2s ease'
                }}
                >
                  motroh
                </h3>
              </Link>
            </div>
            
            {/* Search Bar */}
            <InputGroup style={{ flex: 1, maxWidth: '400px' }}>
              <FormControl
                placeholder="Search for artists, albums, or songs"
                value={searchInput}
                onKeyDown={(e) => e.key === 'Enter' && search()}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <InputGroup.Text style={{ cursor: 'pointer' }} onClick={search}>
                <Search />
              </InputGroup.Text>
            </InputGroup>

            
            {/* Navigation */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginLeft: 'auto' }}>
              {/* Profile Dropdown */}
              {!isLoading && user ? (
                <Dropdown>
                  <Dropdown.Toggle variant="outline-secondary" id="dropdown-basic">
                    {user?.profileImage ? (
                      <img
                        src={
                          user.profileImage.startsWith('http')
                            ? user.profileImage
                            : `http://localhost:5000${user.profileImage}`
                        }
                        alt="Profile"
                        className="nav-avatar"
                        style={{
                          width: 24, height: 24, borderRadius: '50%', objectFit: 'cover',
                          marginRight: 8, verticalAlign: 'middle'
                        }}
                      />
                    ) : (
                      <span
                        className="nav-avatar-fallback"
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 24, height: 24, borderRadius: '50%',
                          background: '#007bff', color: '#fff', fontSize: 12, fontWeight: 700,
                          marginRight: 8
                        }}
                        aria-hidden="true"
                        title="Add a profile photo"
                      >
                        {user?.username?.[0]?.toUpperCase() ?? '?'}
                      </span>
                    )}
                    {user.username}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item as={Link} to="/profile">My Profile</Dropdown.Item>
                    <Dropdown.Item as={Link} to="/settings">Settings</Dropdown.Item>
                    <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              ) : !isLoading ? (
                <>
                  <Button
                    variant="outline-primary"
                    onClick={switchToRegister}
                  >
                    Sign up
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={switchToLogin}
                  >
                    Log in
                  </Button>
                </>
              ) : null}
            </div>
          </div>

          {/* Main Content Area */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <Routes>
              <Route
                path="/"
                element={
                  <div style={{ height: '100%' }}>
                    {(topResult.length > 0 || artists.length > 0 || albums.length > 0 || tracks.length > 0) ? (
                      <Container fluid style={{ padding: '20px' }}>
                        {topResult.length > 0 && (
                          <>
                            <Row className="align-items-start">
                              <Col md={4}>
                                {/*Top Result text header*/}
                                <h5 className="text-start mb-3 fw-bold">Top Result</h5>
                              </Col>
                              <Col md={8}>
                                {/*Songs text header*/}
                                <h5 className="text-start mb-3 fw-bold">
                                  <Link
                                    to="/songs"
                                    style={{
                                      textDecoration: 'none',
                                      color: 'inherit',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Songs
                                  </Link>
                                </h5>
                              </Col>
                            </Row>
                            <Row className="mb-4">
                              <Col md={4}>
                                {/*Top Result card*/}
                                {renderTopResultCard(topResult[0], topResult[0].type)}
                              </Col>
                              {/*Better Scrollable Songs card*/}
                              <Col md={6}>
                                <div
                                  style={{
                                    overflowX: 'auto',
                                    display: 'flex',
                                    gap: '1rem',
                                    scrollSnapType: 'x mandatory',
                                  }}
                                >
                                  {
                                    [...Array(Math.ceil((tracks.length) / 3))].map((_, pageIndex) => {
                                      const start = pageIndex * 3;
                                      const group = tracks.slice(start, start + 3);
            
                                      return (
                                        <div
                                          key={pageIndex}
                                          style={{
                                            minWidth: 'calc(50% - 0.5rem)',
                                            scrollSnapAlign: 'start',
                                          }}
                                        >
                                          <Row className="g-0">
                                            {group.map((track) => (
                                              <Col xs={12} key={track.id} className="mb-3">
                                                <SmallSongCard track={track} />
                                              </Col>
                                            ))}
                                          </Row>
                                        </div>
                                      );
                                    })
                                  }
                                </div>
                              </Col>

                            </Row>
                          </>
                        )}
                        {artists.length > 0 && (
                          <>
                            <h5 className="text-start mb-3 fw-bold">Artists</h5>
                            <div className="hstrip-7 mb-4">
                              {artists.map((artist) => renderCard(artist, 'artist'))}
                            </div>
                          </>
                        )}
                        {albums.length > 0 && (
                          <>
                            <h5 className="text-start mb-3 fw-bold">Albums</h5>
                            <div className="hstrip-7 mb-4">
                              {albums.map((album) => renderCard(album, 'album'))}
                            </div>
                          </>
                        )}
                      </Container>
                    ) : (
                      <HomePage accessToken={accessToken} currentUser={user} />
                    )}
                  </div>
                }
              />
              <Route path="/login" element={<Login onLogin={handleLogin} onSwitchToRegister={switchToRegister} />} />
              <Route path="/register" element={<Register onRegister={handleRegister} onSwitchToLogin={switchToLogin} />} />
              <Route path="/album/:id" element={<AlbumDetail accessToken={accessToken} currentUser={user} />} />
              <Route path="/track/:id" element={<SongDetail accessToken={accessToken} currentUser={user} />} />
              <Route path="/artist/:id" element={<ArtistDetail />} />
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/user/:userId" element={<UserProfileView currentUser={user} />} />
              <Route path="/songs" element={<SongsListPage songs={tracks} />} />
            </Routes>
          </div>
        </div>
      </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
